/**
 * space_input.ts — WHO OWNS A KEY in the sector (space campaign B, 2026-09-30; report §22.1). Pure except the two
 * tiny settings accessors at the foot.
 *
 * The sector's keydown handler used to act on every key whose target was not an INPUT / TEXTAREA: a composing IME
 * character, a SELECT, the Esc Options menu, the dock window (the server said DOCKED and W still ramped the throttle),
 * a focused button (Tab was `preventDefault`ed, so the Options menu could not be walked with the keyboard in space).
 * One predicate now decides, in this order:
 *   1. TEXT ENTRY owns everything: a composing IME key (`isComposing`, or the legacy keyCode 229 an IME reports — MDN:
 *      compositionstart can fire AFTER the first keydown, so isComposing alone misses it), an INPUT / TEXTAREA /
 *      SELECT, or a contentEditable element;
 *   2. the Esc OPTIONS MENU is a modal: while it is up the sector's keys pause (held keys are released);
 *   3. a FOCUSED CONTROL inside a UI surface (the ops panel, the dock, a chart, any window) keeps the keys the browser
 *      gives a control — Tab, Space, Enter, the arrows, Escape — so keyboard focus navigation works (WCAG 2.1.1);
 *      every other key still reaches the sector, exactly as the ground lets you walk with a window focused;
 *   4. otherwise the sector, in its current FRAME: flying, walking a station / the deck, or docked (lib/keybinds.ts
 *      KEY_FRAMES — each frame has its own actions, so a docked pilot's W never flies and a walker's R never fires).
 * Held input is released whenever the page stops hearing key-ups (window blur, the tab hidden), the pointer lock is
 * lost, or a UI takes the keys — the stuck-key lesson every web game learns (MDN Page Visibility; the stuck-key PRs).
 */
import type { KeyFrameName } from './keybinds.js';

export interface KeyOwnerFacts {
  /** The event target's tag, upper-case ('' for none). */
  tag: string;
  /** The target is contentEditable. */
  editable: boolean;
  /** `KeyboardEvent.isComposing`, or `keyCode === 229`. */
  composing: boolean;
  /** The UI surface holding keyboard focus: '' (the page / the canvas) or its name ('ops', 'dock', 'chart', 'window'). */
  focusUi: string;
  /** The Esc Options menu is open. */
  optionsOpen: boolean;
  /** Walking a station promenade or the Sovereign deck. */
  interior: 'station' | 'deck' | null;
  /** The dock window is open (the server's DOCKED mode). */
  docked: boolean;
}

export type KeyOwner =
  | { owner: 'text' }
  | { owner: 'ui'; ui: string }
  | { owner: 'sector'; frame: KeyFrameName };

/** The keys a focused control keeps (the browser's own control keys). */
export const NATIVE_UI_CODES: ReadonlySet<string> = new Set([
  'Tab', 'Space', 'Enter', 'NumpadEnter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Escape',
]);

/** The sector's frame: walking, docked, or flying. */
export function sectorFrame(f: { interior: 'station' | 'deck' | null; docked: boolean }): KeyFrameName {
  return f.interior ? 'sectorWalk' : f.docked ? 'sectorDocked' : 'sector';
}

/** Who owns `code` given where focus is. */
export function sectorKeyOwner(f: KeyOwnerFacts, code: string): KeyOwner {
  if (f.composing) return { owner: 'text' };
  if (f.tag === 'INPUT' || f.tag === 'TEXTAREA' || f.tag === 'SELECT' || f.editable) return { owner: 'text' };
  if (f.optionsOpen) return { owner: 'ui', ui: 'options' };
  if (f.focusUi && NATIVE_UI_CODES.has(code)) return { owner: 'ui', ui: f.focusUi };
  return { owner: 'sector', frame: sectorFrame(f) };
}

/** An IME is composing this key (MDN: check both — isComposing can be false on the first/last key of a composition). */
export function isComposingKey(e: { isComposing?: boolean; keyCode?: number }): boolean {
  return e.isComposing === true || e.keyCode === 229;
}

/** The events after which the page can no longer trust its held-key set. */
export type ReleaseCause = 'blur' | 'hidden' | 'pointer-unlock' | 'ui' | 'frame';
export function releasesHeld(cause: ReleaseCause): boolean {
  return cause === 'blur' || cause === 'hidden' || cause === 'pointer-unlock' || cause === 'ui' || cause === 'frame';
}

// ── The one non-competitive hold: the utility beam (mine / salvage / strip / recover) ──
// XAG 107: "for game controls that must typically be held down … consider allowing players to toggle". The server
// meters the same work either way (spaceMine's cadence, the lot, the hold) — a toggle is not a faster beam.
export type BeamMode = 'hold' | 'toggle';
const BEAM_LS = 'atlas:space-beam';

/** Is the beam on this frame? `held` = the key is down; `latched` = the toggle's state. */
export function beamOn(mode: BeamMode, held: boolean, latched: boolean): boolean {
  return mode === 'toggle' ? latched : held;
}

export function beamMode(): BeamMode {
  try { return localStorage.getItem(BEAM_LS) === 'toggle' ? 'toggle' : 'hold'; } catch { return 'hold'; }
}
export function setBeamMode(m: BeamMode): void {
  try { if (m === 'toggle') localStorage.setItem(BEAM_LS, 'toggle'); else localStorage.removeItem(BEAM_LS); } catch { /* private mode */ }
}
